import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { GradeBadge } from "./grade-badge";
import { GradeTierBadge, type GradeTier } from "./grade-tier-badge";
import { useQuery } from "@tanstack/react-query";
import { resolveCategoryAttributeSchema, formatAttributeChip } from "@shared/categoryAttributes";

function tierForGradeCode(code: string): GradeTier {
  const cx = (code || "").toUpperCase().trim();
  if (cx === "X" || cx === "FACTORY REFURBISHED") return "refurb";
  return _tierForGradeCodeBase(code);
}
function _tierForGradeCodeBase(code: string): GradeTier {
  const c = (code || "").toUpperCase().trim();
  if (c === "A" || c === "TIER 1" || c === "T1" || c === "B" || c === "B (FACTORY REFURBISHED)") return "high";
  if (c === "B+" || c === "TIER 2" || c === "T2") return "mid";
  if (c === "B-" || c === "TIER 3" || c === "T3" || c === "C") return "low";
  return "mid";
}
import { Link } from "wouter";
import { formatMoney, parsePrice } from "@shared/currency";
import { trackSelectItem } from "@/lib/analytics";
import { resolveProductImage, type ProductTypeImage } from "@/lib/productTypeImages";
import { optimizedSrcSet } from "@/lib/imageOptimize";
import { format, parseISO } from "date-fns";
import { Clock, Truck, Sparkles } from "lucide-react";
import {
  getDisplayBrand,
  isFactoryRefurbished,
  FACTORY_REFURBISHED_LABEL,
} from "@shared/palletDisplay";
import { PalletMiniHero } from "./pallet-composition-card";

// Safe JSON parser for attributes - returns empty object on error
function safeParseAttributes(attributes: any): Record<string, any> {
  if (!attributes) return {};
  if (typeof attributes === 'object') return attributes;
  try {
    return JSON.parse(attributes);
  } catch (e) {
    console.warn('Failed to parse product attributes:', e);
    return {};
  }
}

interface ProductCardProps {
  id: string;
  brand: string;
  modelName: string;
  description: string;
  imageUrl?: string;
  startingPrice: number;
  availableGrades: Array<"A" | "B+" | "B-">;
  gradePrices: Record<string, number>;
  viewMode?: 'grid' | 'gallery';
  color?: string;
  dimensionsWidth?: number;
  dimensionsDepth?: number;
  dimensionsHeight?: number;
  weightLbs?: string;
  msrp?: string;
  attributes?: any;
  isSoldOut?: boolean;
  isComingSoonOnly?: boolean;
  earliestShipDate?: string | null;
  category?: string;
  minOrderQty?: number;
  orderMultiple?: number;
  remoteWarehouseCodes?: string[];
  // Pallet-specific props (Phase-1 Virtual AC Pallets). When `isPallet` is true
  // the card displays the manufacturer-mix instead of the seller brand
  // ("OpenBox") and shows a "Factory Refurbished" badge in place of the
  // open-box A/B+/B- grade pills.
  isPallet?: boolean;
  palletConstituentBrands?: string[];
  palletConstituentImages?: string[];
  palletConstituentModelCount?: number;
  /** Admin-uploaded hero photo for the pallet. When set the catalog card
   *  shows this real photo instead of the synthetic 4-tile composite. */
  palletHeroImageUrl?: string | null;
  palletTotalUnits?: number;
  palletConstituents?: Array<{
    brand: string;
    mfgSku: string;
    descriptor?: string | null;
    quantity: number;
    btu: string | null;
    msrpCents: number;
  }>;
  palletMsrpTotalCents?: number;
  palletGrade?: string | null;
  /** Cheapest constituent customer-facing unit price for this pallet, in cents.
   *  Drives the "from $X per item" line shown next to "Starting at" — mirrors
   *  the per-item pill on the showroom landing tile (Task #126). */
  palletPerItemStartingPriceCents?: number | null;
  /** Total in-stock units summed across this model's inventory rows. Drives
   *  the "N pallets available" / "N units available" line under the
   *  wholesale price (Task #156 follow-up). */
  availableQuantity?: number;
  listContext?: {
    listId?: string;
    listName?: string;
    index?: number;
  };
  productTypeImages?: ProductTypeImage[];
  // When true, render universal tier-color grade pills (green/amber/red)
  // instead of the legacy code-specific GradeBadge. Used by the new
  // category-first browse pages (Task #111).
  useTierBadges?: boolean;
  /** When set, the PDP "Back to Products" link will return to this URL
   *  (passed via `?from=` so the PDP can read it). Lets category browse
   *  pages return the user to the same category + filters. */
  backHref?: string;
}

export function ProductCard({
  id,
  brand,
  modelName,
  description,
  imageUrl,
  startingPrice,
  availableGrades,
  gradePrices,
  viewMode = 'grid',
  color,
  dimensionsWidth,
  dimensionsDepth,
  dimensionsHeight,
  weightLbs,
  msrp,
  attributes,
  isSoldOut = false,
  isComingSoonOnly = false,
  earliestShipDate,
  category,
  minOrderQty,
  orderMultiple,
  remoteWarehouseCodes,
  isPallet,
  palletConstituentBrands,
  palletConstituentImages,
  palletConstituentModelCount,
  palletHeroImageUrl,
  palletTotalUnits,
  palletConstituents,
  palletMsrpTotalCents,
  palletGrade,
  palletPerItemStartingPriceCents,
  availableQuantity,
  listContext,
  productTypeImages,
  useTierBadges = false,
  backHref,
}: ProductCardProps) {
  // Reuse the shared pallet helpers: pallets render the manufacturer mix
  // (LG / Frigidaire / Midea / Hisense) instead of the seller brand "OpenBox"
  // and a single "Factory Refurbished" pill instead of generic open-box grades.
  const productLike = {
    brand,
    isPallet,
    palletConstituentBrands,
    attributes,
  };
  const displayBrand = getDisplayBrand(productLike);
  const showFactoryRefurbished = isFactoryRefurbished(productLike);
  const imageHeight = viewMode === 'grid' ? 'h-64' : 'h-96';
  
  const resolvedImageUrl = resolveProductImage(imageUrl, description, brand, productTypeImages);

  // Per-category attribute schema (Task #117) — drives the spec chips below.
  const { data: categoriesData } = useQuery<any[]>({ queryKey: ['/api/categories'] });
  const categoryRow = category
    ? categoriesData?.find((c: any) => c?.name === category)
    : undefined;
  const attributeSchema = resolveCategoryAttributeSchema(category, categoryRow?.attributesSchema);
  const referencePriceLabel: string =
    (categoryRow?.referencePriceLabel as string | null | undefined)?.trim() || "MSRP";

  const formattedShipDate = earliestShipDate
    ? format(typeof earliestShipDate === 'string' ? parseISO(earliestShipDate) : new Date(earliestShipDate), 'MMM d, yyyy')
    : null;
  
  // Build specifications list
  const specs: string[] = [];
  
  if (color) {
    specs.push(color);
  }
  
  // Add key attributes from JSONB — data-driven by the category schema (Task #117).
  if (attributes && attributeSchema.length > 0) {
    const attrs = safeParseAttributes(attributes);
    for (const field of attributeSchema) {
      const chip = formatAttributeChip(field, attrs[field.key]);
      if (chip) specs.push(chip);
    }
  }
  
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackSelectItem(
      {
        id,
        modelNumber: modelName,
        brand,
        category,
        price: startingPrice,
      },
      listContext
    );
  };

  return (
    <Link href={backHref ? `/product/${id}?from=${encodeURIComponent(backHref)}` : `/product/${id}`} onClick={handleClick}>
      <Card className={`product-card overflow-hidden cursor-pointer hover:shadow-lg transition-shadow h-full ${isSoldOut ? 'bg-red-50 dark:bg-red-950/30' : ''}`} data-testid={`product-card-${id}`}>
        {/* Image container - responsive height */}
        <div className="relative bg-muted">
          {isPallet ? (
            // Cap pallet image area max-width so it scales proportionally
            // instead of stretching full-width on large screens. The
            // PalletMiniHero badge slot now hosts the "Factory Refurbished"
            // pill alongside "N pcs · M SKUs" instead of overlaying it.
            <div className={`w-full h-36 sm:h-48 md:h-56 lg:${imageHeight} max-w-md mx-auto`} data-testid={`product-image-${id}`}>
              <PalletMiniHero
                images={palletConstituentImages || []}
                modelCount={palletConstituentModelCount}
                totalUnits={palletTotalUnits}
                heroImageUrl={palletHeroImageUrl ?? null}
                badgeSlot={showFactoryRefurbished ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-emerald-500 bg-emerald-100 text-emerald-800 shadow-sm"
                    data-testid={`badge-factory-refurbished-${id}`}
                    title="Factory-refurbished by the manufacturer to like-new spec"
                  >
                    <Sparkles className="h-2.5 w-2.5" />
                    {FACTORY_REFURBISHED_LABEL}
                  </span>
                ) : null}
              />
            </div>
          ) : (
            <picture>
              <source
                type="image/webp"
                srcSet={optimizedSrcSet(resolvedImageUrl, [200, 400, 800]) || undefined}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              />
              <img
                src={resolvedImageUrl}
                alt={`${brand} ${modelName}`}
                width={400}
                height={300}
                loading="lazy"
                decoding="async"
                className={`w-full h-36 sm:h-48 md:h-56 lg:${imageHeight} object-contain`}
                data-testid={`product-image-${id}`}
              />
            </picture>
          )}
          {/* Coming Soon banner */}
          {isComingSoonOnly && (
            <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-1.5 bg-amber-500 text-white text-xs font-semibold px-2 py-1">
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>
                {formattedShipDate ? `Ships ${formattedShipDate}` : 'Coming Soon'}
              </span>
            </div>
          )}
          {/* Condition badge: A/B+/B- open-box grade pills for non-pallet
              products. Pallets render Factory Refurbished inside PalletMiniHero
              alongside the "N pcs · M SKUs" pill (handled above) so it no
              longer overlays the constituent image grid. */}
          {!isComingSoonOnly && !isPallet && (
            <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 flex flex-col gap-1 sm:gap-2">
              {showFactoryRefurbished ? (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 shadow-sm"
                  data-testid={`badge-factory-refurbished-${id}`}
                  title="Factory-refurbished by the manufacturer to like-new spec"
                >
                  <Sparkles className="h-3 w-3" />
                  {FACTORY_REFURBISHED_LABEL}
                </span>
              ) : useTierBadges ? (
                availableGrades.map((grade) => (
                  <GradeTierBadge key={grade} code={grade} tier={tierForGradeCode(grade)} />
                ))
              ) : (
                availableGrades.map((grade) => (
                  <GradeBadge
                    key={grade}
                    grade={grade}
                    price={gradePrices[grade]}
                    compact={true}
                  />
                ))
              )}
            </div>
          )}
          {isComingSoonOnly && !isPallet && (showFactoryRefurbished || availableGrades.length > 0) && (
            <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 flex flex-col gap-1 sm:gap-2">
              {showFactoryRefurbished ? (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border-2 border-emerald-500 bg-emerald-100 text-emerald-800 shadow-sm"
                  data-testid={`badge-factory-refurbished-${id}`}
                  title="Factory-refurbished by the manufacturer to like-new spec"
                >
                  <Sparkles className="h-3 w-3" />
                  {FACTORY_REFURBISHED_LABEL}
                </span>
              ) : useTierBadges ? (
                availableGrades.map((grade) => (
                  <GradeTierBadge key={grade} code={grade} tier={tierForGradeCode(grade)} />
                ))
              ) : (
                availableGrades.map((grade) => (
                  <GradeBadge
                    key={grade}
                    grade={grade as "A" | "B+" | "B-"}
                    compact={true}
                  />
                ))
              )}
            </div>
          )}
        </div>
        <CardContent className="p-2 sm:p-3 md:p-4">
          {/* Brand line: hidden for pallets so the multi-brand nature is
              communicated by the constituent bullet list below instead of a
              redundant "MULTI-BRAND PALLET" eyebrow. */}
          {!isPallet && (
            <p
              className="text-[10px] sm:text-xs text-muted-foreground mb-0.5 sm:mb-1 uppercase tracking-wide truncate"
              data-testid={`product-brand-${id}`}
            >
              {displayBrand}
            </p>
          )}
          {/* Model name - truncate on mobile */}
          <h3 className="font-semibold text-foreground mb-1 sm:mb-2 text-sm sm:text-base md:text-[18px] line-clamp-2" data-testid={`product-title-${id}`}>
            {modelName}
          </h3>
          {/* Description vs. pallet bullet list */}
          {isPallet && palletConstituents && palletConstituents.length > 0 ? (
            <ul
              className="mb-1 sm:mb-2 text-xs sm:text-sm md:text-[15px] text-muted-foreground space-y-0.5 hidden sm:block"
              data-testid={`pallet-constituents-${id}`}
            >
              {[...palletConstituents].sort((a, b) => b.quantity - a.quantity).map((c, i) => (
                <li key={`${c.mfgSku}-${i}`} className="flex items-start gap-1.5">
                  <span className="text-muted-foreground/60 mt-[1px]">•</span>
                  <span className="flex-1 leading-snug">
                    <span className="font-semibold text-foreground tabular-nums">{c.quantity}×</span>{" "}
                    <span className="font-medium text-foreground">{c.brand}</span>{" "}
                    <span className="font-mono text-[0.92em]">{c.mfgSku}</span>
                    {c.descriptor && (
                      <span className="text-foreground/80"> {c.descriptor}</span>
                    )}
                    {c.btu && <span className="text-muted-foreground"> · {c.btu} BTU</span>}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground mb-1 sm:mb-2 line-clamp-2 text-xs sm:text-sm md:text-[16px] hidden sm:block">
              {description}
            </p>
          )}
          {/* Specs - hidden on mobile */}
          {specs.length > 0 && (
            <div className="hidden md:flex text-xs text-muted-foreground mb-3 flex-wrap gap-x-3 gap-y-1" data-testid={`product-specs-${id}`}>
              {specs.slice(0, 4).map((spec, idx) => (
                <span key={idx} className="flex items-center">
                  {idx > 0 && <span className="mr-3 text-muted-foreground/50">•</span>}
                  {spec}
                </span>
              ))}
            </div>
          )}
          {/* Remote warehouse "Ships from {code}" label */}
          {remoteWarehouseCodes && remoteWarehouseCodes.length > 0 && (
            <div className="flex items-center gap-1 mb-2" data-testid={`product-ships-from-${id}`}>
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5">
                <Truck className="w-3 h-3" />
                Ships from {remoteWarehouseCodes.join(" / ")}
              </span>
            </div>
          )}
          {/* Ordering rules label */}
          {((minOrderQty && minOrderQty > 1) || (orderMultiple && orderMultiple > 1)) && (
            <div className="flex items-center gap-1 mb-2" data-testid={`product-ordering-rules-${id}`}>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded px-1.5 py-0.5">
                {[
                  minOrderQty && minOrderQty > 1 ? `Min. ${minOrderQty} units` : null,
                  orderMultiple && orderMultiple > 1 ? `Order in multiples of ${orderMultiple}` : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          {/* Pricing / Coming Soon */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mt-auto">
            {isComingSoonOnly ? (
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Reserve Your Unit</p>
                <p className="text-sm sm:text-base md:text-lg font-bold text-amber-600 dark:text-amber-400" data-testid={`product-price-${id}`}>
                  Coming Soon
                </p>
              </div>
            ) : (
              <div className="flex gap-3 sm:gap-6 items-center">
                {(() => {
                  // For pallets, MSRP is the sum of every constituent's MSRP
                  // multiplied by its line quantity (e.g. 4× $200 + 4× $250).
                  // For everything else, fall back to the model's own MSRP.
                  const effectiveMsrp =
                    isPallet && palletMsrpTotalCents && palletMsrpTotalCents > 0
                      ? palletMsrpTotalCents / 100
                      : msrp
                        ? parsePrice(msrp)
                        : 0;
                  if (!effectiveMsrp) return null;
                  return (
                    <div className="hidden sm:block">
                      <p className="text-[10px] sm:text-xs text-muted-foreground">{referencePriceLabel}</p>
                      <p
                        className="text-muted-foreground line-through font-bold text-sm sm:text-base md:text-[18px]"
                        data-testid={`product-msrp-${id}`}
                      >
                        {formatMoney(effectiveMsrp)}
                      </p>
                    </div>
                  );
                })()}
                <div>
                  {/* "Wholesale" label sits above the green price as the
                      mirror of the "Retail" label above the struck MSRP.
                      For non-pallet SKUs we keep the legacy "Starting at"
                      copy so refrigerator cards aren't disrupted.
                      SharkNinja / Small-Appliances pallets (palletGrade='X')
                      previously hid the label entirely because "Starting at"
                      was misleading at a single fixed price; "Wholesale"
                      reads correctly there too. */}
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {isPallet ? 'Wholesale' : 'Starting at'}
                  </p>
                  <p className="text-sm sm:text-base md:text-lg font-bold text-primary" data-testid={`product-price-${id}`}>
                    {formatMoney(parsePrice(startingPrice))}
                  </p>
                  {/* "N pallets available" / "N units available" — the
                      noun is "pallet" for synthetic pallet SKUs and "unit"
                      for everything else. Hidden when we don't have a
                      count (legacy callers that don't pass the prop). */}
                  {availableQuantity != null && availableQuantity > 0 && (
                    <p
                      className="text-[10px] sm:text-xs text-muted-foreground mt-0.5"
                      data-testid={`product-available-quantity-${id}`}
                    >
                      {availableQuantity}{' '}
                      {isPallet
                        ? availableQuantity === 1 ? 'pallet' : 'pallets'
                        : availableQuantity === 1 ? 'unit' : 'units'}{' '}
                      available
                    </p>
                  )}
                  {/* Pallet per-item price — matches the showroom landing
                      tile copy ("from $X per item"). Only shown for pallets
                      with a known cheapest-constituent unit price. */}
                  {isPallet && palletGrade !== 'X' && palletPerItemStartingPriceCents != null && palletPerItemStartingPriceCents > 0 && (
                    <p
                      className="text-[10px] sm:text-xs text-muted-foreground mt-0.5"
                      data-testid={`product-per-item-price-${id}`}
                    >
                      from {formatMoney(palletPerItemStartingPriceCents / 100)} per item
                    </p>
                  )}
                </div>
              </div>
            )}
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto touch-target text-xs sm:text-sm"
              size="sm"
              data-testid={`button-view-details-${id}`}
            >
              <span className="sm:hidden">View</span>
              <span className="hidden sm:inline">&gt;</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
